import { createLngLat, type LngLat } from './coordinates.ts';

declare const trailIdBrand: unique symbol;
declare const trailDataVersionBrand: unique symbol;

export type TrailId = string & { readonly [trailIdBrand]: true };
export type TrailDataVersion = string & { readonly [trailDataVersionBrand]: true };
export type TrailDifficulty = '3' | '4' | '9' | '10';

export interface LineStringGeometry {
  readonly type: 'LineString';
  readonly coordinates: readonly LngLat[];
}

export interface MultiLineStringGeometry {
  readonly type: 'MultiLineString';
  readonly coordinates: readonly (readonly LngLat[])[];
}

export type TrailGeometry = LineStringGeometry | MultiLineStringGeometry;

export interface Trail {
  readonly id: TrailId;
  readonly title: string;
  readonly difficulty: TrailDifficulty;
  readonly geometry: TrailGeometry;
  readonly externalUrl?: string;
}

export interface CanonicalTrailFeature {
  readonly type: 'Feature';
  readonly id: TrailId;
  readonly geometry: TrailGeometry;
  readonly properties: {
    readonly trailId: TrailId;
    readonly title: string;
    readonly difficulty: TrailDifficulty;
    readonly externalUrl?: string;
  };
}

export interface CanonicalTrailCollection {
  readonly type: 'FeatureCollection';
  readonly features: readonly CanonicalTrailFeature[];
}

const trailDataVersionPattern = /^[0-9a-f]{64}$/u;
export const selectableTrailDifficulties = Object.freeze(['3', '4', '9', '10'] as const);

const supportedDifficulties = new Set<TrailDifficulty>(selectableTrailDifficulties);

function hasControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit <= 31 || codeUnit === 127) return true;
  }
  return false;
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function compareText(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

export function createTrailId(value: unknown): TrailId {
  if (
    typeof value !== 'string' ||
    value.trim() !== value ||
    value.length === 0 ||
    value.length > 128 ||
    hasControlCharacter(value)
  ) {
    throw new TypeError('trail ID must be non-blank opaque text');
  }
  return value as TrailId;
}

export function createTrailDataVersion(value: unknown): TrailDataVersion {
  if (typeof value !== 'string' || !trailDataVersionPattern.test(value)) {
    throw new TypeError('trail data version must be a lowercase SHA-256 digest');
  }
  return value as TrailDataVersion;
}

export function parseTrailDifficulty(value: unknown): TrailDifficulty | undefined {
  return typeof value === 'string' && supportedDifficulties.has(value as TrailDifficulty)
    ? (value as TrailDifficulty)
    : undefined;
}

function parseLineCoordinates(value: unknown, path: string): readonly LngLat[] {
  if (!Array.isArray(value) || value.length < 2) {
    throw new TypeError(`${path} must contain at least two coordinates`);
  }
  return Object.freeze(
    value.map((coordinate, index) => {
      if (!Array.isArray(coordinate) || coordinate.length !== 2) {
        throw new TypeError(`${path}[${String(index)}] must be [longitude, latitude]`);
      }
      const longitude: unknown = coordinate[0];
      const latitude: unknown = coordinate[1];
      if (typeof longitude !== 'number' || typeof latitude !== 'number') {
        throw new TypeError(`${path}[${String(index)}] must contain numbers`);
      }
      return createLngLat(longitude, latitude);
    }),
  );
}

function parseGeometry(value: unknown, path: string): TrailGeometry {
  if (!isRecord(value)) {
    throw new TypeError(`${path} must be an object`);
  }
  if (value['type'] === 'LineString') {
    return Object.freeze({
      type: 'LineString',
      coordinates: parseLineCoordinates(value['coordinates'], `${path}.coordinates`),
    });
  }
  if (value['type'] === 'MultiLineString') {
    if (!Array.isArray(value['coordinates']) || value['coordinates'].length === 0) {
      throw new TypeError(`${path}.coordinates must contain at least one line`);
    }
    return Object.freeze({
      type: 'MultiLineString',
      coordinates: Object.freeze(
        value['coordinates'].map((line, index) =>
          parseLineCoordinates(line, `${path}.coordinates[${String(index)}]`),
        ),
      ),
    });
  }
  throw new TypeError(`${path}.type must be LineString or MultiLineString`);
}

function parseExternalUrl(value: unknown, path: string): string | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  if (typeof value !== 'string') {
    throw new TypeError(`${path} must be a URL string`);
  }
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new TypeError(`${path} must be a valid URL`);
  }
  if (
    parsed.protocol !== 'https:' ||
    parsed.hostname !== 'www.trailforks.com' ||
    parsed.username !== '' ||
    parsed.password !== ''
  ) {
    throw new TypeError(`${path} must be an HTTPS Trailforks URL`);
  }
  return parsed.href;
}

export function normalizeSelectableTrails(source: unknown): readonly Trail[] {
  if (
    !isRecord(source) ||
    source['type'] !== 'FeatureCollection' ||
    !Array.isArray(source['features'])
  ) {
    throw new TypeError('trail source must be a GeoJSON FeatureCollection');
  }
  if ('crs' in source) {
    throw new TypeError('trail source must use RFC 7946 WGS84 without a crs member');
  }

  const trails: Trail[] = [];
  const ids = new Set<TrailId>();
  source['features'].forEach((feature, index) => {
    const path = `features[${String(index)}]`;
    if (!isRecord(feature) || feature['type'] !== 'Feature' || !isRecord(feature['properties'])) {
      throw new TypeError(`${path} must be a GeoJSON Feature with properties`);
    }
    const activity = feature['properties']['activitytype'];
    if (typeof activity !== 'number' || !Number.isInteger(activity)) {
      throw new TypeError(`${path}.properties.activitytype must be an integer`);
    }
    const difficultyValue = feature['properties']['difficulty'];
    if (typeof difficultyValue !== 'string') {
      throw new TypeError(`${path}.properties.difficulty must be text`);
    }
    const difficulty = parseTrailDifficulty(difficultyValue);
    const id = createTrailId(feature['properties']['trailid']);
    const title = feature['properties']['title'];
    if (typeof title !== 'string' || title.trim() === '') {
      throw new TypeError(`${path}.properties.title must be non-blank text`);
    }
    const externalUrl = parseExternalUrl(
      feature['properties']['trailforksurl'],
      `${path}.properties.trailforksurl`,
    );
    const geometry = parseGeometry(feature['geometry'], `${path}.geometry`);
    if ((activity !== 0 && activity !== 1) || difficulty === undefined) {
      return;
    }
    if (ids.has(id)) {
      throw new TypeError(`duplicate selectable trail ID: ${id}`);
    }
    ids.add(id);
    trails.push(
      Object.freeze({
        id,
        title,
        difficulty,
        geometry,
        ...(externalUrl === undefined ? {} : { externalUrl }),
      }),
    );
  });

  trails.sort((left, right) => compareText(left.id, right.id));
  return Object.freeze(trails);
}

export function toCanonicalTrailCollection(trails: readonly Trail[]): CanonicalTrailCollection {
  return Object.freeze({
    type: 'FeatureCollection',
    features: Object.freeze(
      trails.map((trail) =>
        Object.freeze({
          type: 'Feature',
          id: trail.id,
          geometry: trail.geometry,
          properties: Object.freeze({
            trailId: trail.id,
            title: trail.title,
            difficulty: trail.difficulty,
            ...(trail.externalUrl === undefined ? {} : { externalUrl: trail.externalUrl }),
          }),
        }),
      ),
    ),
  });
}
